import { useEffect, useState } from 'react'
import { trackDistAffiliateClick, trackDistEmpty, trackDistError, trackDistView } from '../lib/analytics'
import { resolveAssetUrl } from '../lib/assetUrl'

// Standalone Distribution tiles widget for cross-origin embed via the
// DistributionWidget.init() SDK. Two modes, one grid:
//
//   sport:      the books carrying a sport  (GET /api/v1/sports/:slug/details)
//   sportsbook: the sports a book carries   (GET /api/v1/sportsbooks/:slug/details)
//
// Each payload is mapped to the same tile model (name, brand, logo, link) so
// the rendering below is shared. Owns its own fetch so the SDK only needs
// the key + apiBase.

export interface SportCarrier {
  sportsbook_id: number
  sportsbook_name: string | null
  brand_color: string | null
  logo_url: string | null
  logo_fill: boolean
  division_id: number | null
  division_name: string | null
  affiliate_url: string | null
}

export interface SportDetails {
  id: number
  name: string
  slug: string
  approved_states: string[]
  international: string[]
  carriers: SportCarrier[]
}

export interface SportsbookCarriedSport {
  sport_id: number
  slug: string | null
  name: string
  category: string | null
  logo_url: string | null
  brand_color: string | null
  /** Light logo mark — paint the plain tile black. */
  logo_dark?: boolean
  division_id: number | null
  division_name: string | null
  traded_by: 'us' | 'book'
  affiliate_url: string | null
}

export interface SportsbookDetails {
  id: number
  slug: string
  name: string
  kind: 'sportsbook' | 'dfs' | 'prediction'
  brand_color: string | null
  logo_url: string | null
  logo_fill: boolean
  active_states: string[]
  international: string[]
  blocked_international: string[]
  sports: SportsbookCarriedSport[]
}

interface Tile {
  key: number
  name: string
  /** data-* hook for tests / host CSS: data-book (sport mode) or data-sport (sportsbook mode). */
  dataAttr: 'data-book' | 'data-sport'
  dataValue: string
  brand_color: string | null
  logo_url: string | null
  logo_fill: boolean
  /** Sportsbook mode: light logo mark → black tile instead of transparent. */
  dark: boolean
  href: string | null
}

function slugify(s: string): string {
  return s.toLowerCase().replace(/\s+/g, '-')
}

// Multi-division sports (WoO, X Games) produce one carrier row per division —
// collapse to one tile per book. The API's order is priority-ranked, so the
// first occurrence is the one to keep.
function tilesFromSport(d: SportDetails): Tile[] {
  const seen = new Set<number>()
  const out: Tile[] = []
  for (const c of d.carriers) {
    if (seen.has(c.sportsbook_id)) continue
    seen.add(c.sportsbook_id)
    const name = c.sportsbook_name ?? `#${c.sportsbook_id}`
    out.push({
      key: c.sportsbook_id,
      name,
      dataAttr: 'data-book',
      dataValue: slugify(name),
      brand_color: c.brand_color,
      logo_url: c.logo_url,
      logo_fill: c.logo_fill,
      dark: false,
      href: c.affiliate_url,
    })
  }
  return out
}

function tilesFromSportsbook(d: SportsbookDetails): Tile[] {
  const seen = new Set<number>()
  const out: Tile[] = []
  for (const s of d.sports) {
    if (seen.has(s.sport_id)) continue
    seen.add(s.sport_id)
    out.push({
      key: s.sport_id,
      name: s.name,
      dataAttr: 'data-sport',
      dataValue: s.slug ?? slugify(s.name),
      brand_color: s.brand_color,
      logo_url: s.logo_url,
      logo_fill: false,
      dark: s.logo_dark === true,
      href: s.affiliate_url,
    })
  }
  return out
}

interface Props {
  /** Sport slug — the books carrying it. One of sport / sportsbook is required. */
  sport?: string
  /** Sportsbook slug — the sports it carries. */
  sportsbook?: string
  apiBase: string
  // Cap the grid at the first N tiles — the API's order is already
  // priority-ranked (sport mode) / alphabetical (sportsbook mode). Omit/0 = all.
  maxBooks?: number
  // Sub-affiliate embed flag — forwarded as ?aff= so the API swaps tile
  // links to the sub-affiliate's own per-book URLs.
  subAffiliate?: string
}

export default function DistributionWidget({ sport, sportsbook, apiBase, maxBooks, subAffiliate }: Props) {
  const [tiles, setTiles] = useState<Tile[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const mode: 'sport' | 'sportsbook' = sportsbook ? 'sportsbook' : 'sport'
  const key = sportsbook ?? sport ?? ''
  // GA4 dimension: sport slug in sport mode, `book:<slug>` in sportsbook mode.
  const subject = mode === 'sportsbook' ? `book:${key}` : key

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    if (!key) {
      setError('DistributionWidget.init needs `sport` or `sportsbook`')
      setLoading(false)
      return
    }
    const path = mode === 'sportsbook'
      ? `/api/v1/sportsbooks/${encodeURIComponent(key)}/details`
      : `/api/v1/sports/${encodeURIComponent(key)}/details`
    fetch(`${apiBase}${path}${subAffiliate ? `?aff=${encodeURIComponent(subAffiliate)}` : ''}`)
      .then(async (res) => {
        if (res.status === 404) {
          if (!cancelled) {
            trackDistEmpty(subject)
            setError(mode === 'sportsbook' ? `no sportsbook found for "${key}"` : `no sport found for "${key}"`)
          }
          return null
        }
        if (!res.ok) throw new Error(`fetch failed: ${res.status}`)
        return res.json() as Promise<SportDetails | SportsbookDetails>
      })
      .then((d) => {
        if (cancelled || !d) return
        const next = mode === 'sportsbook' ? tilesFromSportsbook(d as SportsbookDetails) : tilesFromSport(d as SportDetails)
        if (next.length === 0) trackDistEmpty(subject)
        else trackDistView(subject)
        setTiles(next)
      })
      .catch((e) => {
        if (cancelled) return
        trackDistError(subject)
        setError((e as Error).message)
      })
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })
    return () => { cancelled = true }
  }, [mode, key, subject, apiBase, subAffiliate])

  if (loading) return <p className="ad-list-empty b-mono-dim">LOADING…</p>
  if (error) return <p className="ad-list-empty b-mono-dim">⚠ {error}</p>
  if (!tiles) return <p className="ad-list-empty b-mono-dim">no distribution data</p>

  if (tiles.length === 0) {
    return (
      <p className="ad-list-empty b-mono-dim">
        {mode === 'sportsbook' ? 'this book carries no sports yet' : 'no books carry this sport yet'}
      </p>
    )
  }
  const shown = maxBooks && maxBooks > 0 ? tiles.slice(0, maxBooks) : tiles

  return (
    <div className={`ew-distribution${mode === 'sportsbook' ? ' is-sportsbook' : ''}`}>
      <div className="ew-logo-grid">
        {shown.map((t) => {
          const hasLogo = t.logo_url != null
          const hasBrand = t.brand_color != null
          // Sportsbook mode: league logos sit on NO background and are NOT
          // links (Cole, 2026-10-07) — brand colour and affiliate_url stay in
          // the payload but are not rendered.
          const plain = mode === 'sportsbook'
          const href = plain ? null : t.href
          const tileClass = plain
            ? (hasLogo ? `ew-logo-tile is-plain${t.dark ? ' is-dark' : ''}` : 'ew-logo-tile is-text')
            : hasLogo
              ? `ew-logo-tile${t.logo_fill ? ' is-fill' : ''}`
              : hasBrand
                ? 'ew-logo-tile is-brand-text'
                : 'ew-logo-tile is-text'
          const style = !plain && hasBrand ? { background: t.brand_color! } : undefined
          const inner = (
            <div className={tileClass} style={style} title={t.name} {...{ [t.dataAttr]: t.dataValue }}>
              {hasLogo
                ? <img src={resolveAssetUrl(apiBase, t.logo_url)} alt={t.name} loading="lazy" decoding="async" />
                : <span className="ew-logo-tile-text">{t.name}</span>}
            </div>
          )
          return (
            <div key={t.key} className="ew-logo-tile-wrap">
              {href
                ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ew-logo-tile-link"
                      aria-label={`Open ${t.name}`}
                      onClick={() => trackDistAffiliateClick(subject, t.name)}
                    >
                      {inner}
                    </a>
                  )
                : inner}
            </div>
          )
        })}
      </div>
    </div>
  )
}
